import React, { useState, useEffect, useCallback } from 'react';
import GroupCalendar from '../../components/GroupCalendar';
import { useCourses } from '../../contexts/CourseContext';
import { getGroupAvailableDates, adminManageGroupDates } from '../../services/groupBookingService';
import { DateStatus } from '../../types/groupBooking';

const GroupDateManagement: React.FC = () => {
  const { courses } = useCourses();
  const groupPrograms = courses.filter(c => c.category?.includes('group') && c.is_published);

  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dateStatuses, setDateStatuses] = useState<Record<string, DateStatus>>({});
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // 프로그램 선택 시 첫 번째 자동 선택
  useEffect(() => {
    if (groupPrograms.length > 0 && !selectedProgramId) {
      setSelectedProgramId(groupPrograms[0].id);
    }
  }, [groupPrograms, selectedProgramId]);

  const fetchDates = useCallback(async () => {
    if (!selectedProgramId) return;
    setLoading(true);

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;

    const result = await getGroupAvailableDates(selectedProgramId, year, month);
    if (result.success && result.data) {
      const statusMap: Record<string, DateStatus> = {};
      // 가능한 날짜만 selected로 표시
      const available: string[] = [];
      for (const d of result.data.dates) {
        statusMap[d.date] = d.status as DateStatus;
        if (d.status === 'available' || d.status === 'pending') {
          available.push(d.date);
        }
      }
      setDateStatuses(statusMap);
      setSelectedDates(available);
    }
    setLoading(false);
  }, [selectedProgramId, currentMonth]);

  useEffect(() => {
    fetchDates();
  }, [fetchDates]);

  const handleDateToggle = (date: string) => {
    setSelectedDates(prev =>
      prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
    );
  };

  const handleSave = async () => {
    if (!selectedProgramId) return;
    setSaving(true);
    setMessage('');

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();

    // 이번 달 모든 날짜
    const allDates: string[] = [];
    for (let d = 1; d <= lastDay; d++) {
      allDates.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }

    // 추가할 날짜 (선택됨 && 기존에 available/pending이 아니었던)
    const datesToAdd = selectedDates.filter(d => {
      const status = dateStatuses[d];
      return status !== 'available' && status !== 'pending' && status !== 'booked';
    });

    // 제거할 날짜 (선택 해제됨 && 기존에 available/pending이었던)
    const datesToRemove = allDates.filter(d => {
      const status = dateStatuses[d];
      return !selectedDates.includes(d) && (status === 'available' || status === 'pending');
    });

    try {
      if (datesToAdd.length > 0) {
        const addResult = await adminManageGroupDates({
          program_id: selectedProgramId,
          action: 'add',
          dates: datesToAdd,
        });
        if (!addResult.success) {
          setMessage(addResult.error?.message || '날짜 추가에 실패했습니다.');
          setSaving(false);
          return;
        }
      }

      if (datesToRemove.length > 0) {
        // 닫으려는 날짜 중 승인대기 건이 있는지 확인
        const pendingRemoveDates = datesToRemove.filter(d => dateStatuses[d] === 'pending');
        if (pendingRemoveDates.length > 0) {
          const ok = window.confirm(
            `닫으려는 날짜 중 승인대기 신청이 있는 날짜가 ${pendingRemoveDates.length}개 있습니다.\n해당 신청이 자동 취소됩니다. 계속하시겠습니까?`
          );
          if (!ok) {
            setSaving(false);
            return;
          }
        }

        const removeResult = await adminManageGroupDates({
          program_id: selectedProgramId,
          action: 'remove',
          dates: datesToRemove,
        });
        if (!removeResult.success) {
          setMessage(removeResult.error?.message || '날짜 제거에 실패했습니다.');
          setSaving(false);
          return;
        }
      }

      setMessage('저장되었습니다.');
      await fetchDates();
    } catch {
      setMessage('저장 중 오류가 발생했습니다.');
    }

    setSaving(false);
    setTimeout(() => setMessage(''), 3000);
  };

  // 평일 전체 선택
  const selectAllWeekdays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekdays: string[] = [];
    for (let d = 1; d <= lastDay; d++) {
      const date = new Date(year, month, d);
      const dow = date.getDay();
      if (dow >= 1 && dow <= 5 && date >= today) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        // 이미 확정된 날은 제외
        if (dateStatuses[dateStr] !== 'booked') {
          weekdays.push(dateStr);
        }
      }
    }
    setSelectedDates(weekdays);
  };

  const clearAll = () => {
    // 확정 건이 있는 날짜는 유지
    setSelectedDates([]);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">단체체험 날짜 관리</h1>
        <p className="text-gray-600 text-sm">예약 가능한 날짜를 선택하여 관리합니다. 초록색 날짜가 예약 가능 상태입니다.</p>
      </div>

      {/* 프로그램 선택 */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">프로그램 선택</label>
        {groupPrograms.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800 font-medium">등록된 단체체험 프로그램이 없습니다.</p>
            <p className="text-sm text-amber-700 mt-1">강의 관리에서 카테고리에 'group'을 포함한 프로그램을 먼저 등록해주세요.</p>
          </div>
        ) : (
          <select
            value={selectedProgramId}
            onChange={e => setSelectedProgramId(e.target.value)}
            className="w-full max-w-md px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {groupPrograms.map(p => (
              <option key={p.id} value={p.id}>{p.courseName}</option>
            ))}
          </select>
        )}
      </div>

      {/* 빠른 선택 */}
      <div className="flex gap-3 mb-4">
        <button
          onClick={selectAllWeekdays}
          className="px-4 py-2 text-sm font-medium bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
        >
          평일 전체 선택
        </button>
        <button
          onClick={clearAll}
          className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
        >
          전체 해제
        </button>
      </div>

      {/* 캘린더 */}
      <div className="mb-6">
        <GroupCalendar
          currentMonth={currentMonth}
          onMonthChange={setCurrentMonth}
          dateStatuses={dateStatuses}
          selectedDates={selectedDates}
          onDateToggle={handleDateToggle}
          loading={loading}
          adminMode
        />
      </div>

      {/* 저장 */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all ${
            saving
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          }`}
        >
          {saving ? '저장 중...' : '저장'}
        </button>
        {message && (
          <span className={`text-sm font-medium ${message.includes('실패') || message.includes('오류') ? 'text-red-600' : 'text-green-600'}`}>
            {message}
          </span>
        )}
      </div>
    </div>
  );
};

export default GroupDateManagement;
